// Use environment variable for API URL, fallback to /api for development
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const api = {
  // Auth endpoints
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    return handleResponse(response);
  },

  getCurrentUser: async (token) => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Reports endpoints
  getReports: async (token) => {
    const response = await fetch(`${API_BASE_URL}/reports`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  getReport: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  createReport: async (token, reportData) => {
    const response = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(reportData)
    });
    return handleResponse(response);
  },

  updateReport: async (token, id, reportData) => {
    const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(reportData)
    });
    return handleResponse(response);
  },

  deleteReport: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Checklist templates endpoints
  getTemplates: async (token) => {
    const response = await fetch(`${API_BASE_URL}/checklists/templates`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  getTemplate: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/checklists/templates/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  createTemplate: async (token, templateData) => {
    const response = await fetch(`${API_BASE_URL}/checklists/templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(templateData)
    });
    return handleResponse(response);
  },

  // Users endpoints
  getUsers: async (token) => {
    const response = await fetch(`${API_BASE_URL}/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  createUser: async (token, userData) => {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  updateUser: async (token, id, userData) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  deleteUser: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Disable/Enable user endpoints
  disableUser: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/users/admin/${id}/disable`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  enableUser: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/users/admin/${id}/enable`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Email digest endpoints
  enableEmailDigest: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/users/admin/${id}/email-digest/enable`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  disableEmailDigest: async (token, id) => {
    const response = await fetch(`${API_BASE_URL}/users/admin/${id}/email-digest/disable`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // User profile endpoints
  updateUserProfile: async (token, profileData) => {
    const response = await fetch(`${API_BASE_URL}/users/me/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    return handleResponse(response);
  },

  changePassword: async (token, currentPassword, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/users/me/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
    });
    return handleResponse(response);
  },

  // Email preferences endpoints
  getEmailPreferences: async (token) => {
    const response = await fetch(`${API_BASE_URL}/users/me/email-preferences`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  updateEmailPreferences: async (token, preferences) => {
    const response = await fetch(`${API_BASE_URL}/users/me/email-preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(preferences)
    });
    return handleResponse(response);
  },

  // Admin email settings endpoints
  getAdminEmailSettings: async (token) => {
    const response = await fetch(`${API_BASE_URL}/admin/email-settings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  updateAdminEmailSettings: async (token, settings) => {
    const response = await fetch(`${API_BASE_URL}/admin/email-settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  testEmailConnection: async (token) => {
    const response = await fetch(`${API_BASE_URL}/admin/test-email`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  // Report age limit settings endpoints
  getReportAgeLimit: async (token) => {
    const response = await fetch(`${API_BASE_URL}/admin/settings/report-age-limit`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(response);
  },

  updateReportAgeLimit: async (token, days) => {
    const response = await fetch(`${API_BASE_URL}/admin/settings/report-age-limit`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ days })
    });
    return handleResponse(response);
  }
};

async function handleResponse(response) {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.error || 'Something went wrong');
    error.status = response.status;
    throw error;
  }
  return data;
}

// Export individual functions for convenience
export const {
  login,
  getCurrentUser,
  getReports,
  getReport,
  createReport,
  updateReport,
  deleteReport,
  getTemplates,
  getTemplate,
  createTemplate,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  updateUserProfile,
  changePassword,
  getEmailPreferences,
  updateEmailPreferences,
  getAdminEmailSettings,
  updateAdminEmailSettings,
  testEmailConnection,
  getReportAgeLimit,
  updateReportAgeLimit
} = api;
